import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import MessageLoading from './message-loading';
import type { ButtonProps } from './button';
import { Button } from './button';

// ChatBubble
const chatBubbleVariant = cva("bb:flex bb:gap-2 bb:max-w-[60%] bb:items-end bb:relative bb:group", {
    variants: {
        variant: {
            received: "bb:self-start",
            sent: "bb:self-end bb:flex-row-reverse",
        },
        layout: {
            default: "",
            ai: "bb:max-w-full bb:w-full bb:items-center",
        },
    },
    defaultVariants: {
        variant: "received",
        layout: "default",
    },
});

type ChatBubbleProps = {} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof chatBubbleVariant>;

const ChatBubble = React.forwardRef<HTMLDivElement, ChatBubbleProps>(
    ({ className, variant, layout, children, ...props }, ref) => (
        <div
            className={cn(chatBubbleVariant({ variant, layout, className }), "bb:relative bb:group")}
            ref={ref}
            {...props}
        >
            {React.Children.map(children, (child) =>
                React.isValidElement(child) && typeof child.type !== "string"
                    ? React.cloneElement(child, {
                          variant,
                          layout,
                      } as React.ComponentProps<typeof child.type>)
                    : child,
            )}
        </div>
    ),
);
ChatBubble.displayName = 'ChatBubble';

// ChatBubbleAvatar
type ChatBubbleAvatarProps = {
  src?: string;
  fallback?: string;
  className?: string;
};

const ChatBubbleAvatar: React.FC<ChatBubbleAvatarProps> = ({
  src,
  fallback,
  className,
}) => (
  <Avatar className={className}>
    <AvatarImage src={src} alt="Avatar" />
    <AvatarFallback>{fallback}</AvatarFallback>
  </Avatar>
);

// ChatBubbleMessage
const chatBubbleMessageVariants = cva("bb:p-4", {
    variants: {
        variant: {
            received: "bb:bg-secondary bb:text-secondary-foreground bb:rounded-r-lg bb:rounded-tl-lg",
            sent: "bb:bg-primary bb:text-primary-foreground bb:rounded-l-lg bb:rounded-tr-lg",
        },
        layout: {
            default: "",
            ai: "bb:border-t bb:w-full bb:rounded-none bb:bg-transparent",
        },
    },
    defaultVariants: {
        variant: "received",
        layout: "default",
    },
});

type ChatBubbleMessageProps = {
  isLoading?: boolean;
} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof chatBubbleMessageVariants>;

const ChatBubbleMessage = React.forwardRef<HTMLDivElement, ChatBubbleMessageProps>(
    ({ className, variant, layout, isLoading = false, children, ...props }, ref) => (
        <div
            className={cn(
                chatBubbleMessageVariants({ variant, layout, className }),
                "bb:break-words bb:max-w-full bb:whitespace-pre-wrap",
            )}
            ref={ref}
            {...props}
        >
            {isLoading ? (
                <div className="bb:flex bb:items-center bb:space-x-2">
                    <MessageLoading />
                </div>
            ) : (
                children
            )}
        </div>
    ),
);
ChatBubbleMessage.displayName = 'ChatBubbleMessage';

// ChatBubbleTimestamp
type ChatBubbleTimestampProps = {
  timestamp: string;
} & React.HTMLAttributes<HTMLDivElement>;

const ChatBubbleTimestamp: React.FC<ChatBubbleTimestampProps> = ({ timestamp, className, ...props }) => (
    <div className={cn("bb:text-xs bb:mt-2 bb:text-right", className)} {...props}>
        {timestamp}
    </div>
);

// ChatBubbleAction
type ChatBubbleActionProps = ButtonProps & {
  icon: React.ReactNode;
};

const ChatBubbleAction: React.FC<ChatBubbleActionProps> = ({
  icon,
  onClick,
  className,
  variant = 'ghost',
  size = 'icon',
  ...props
}) => (
  <Button
    variant={variant}
    size={size}
    className={className}
    onClick={onClick}
    {...props}>
    {icon}
  </Button>
);

type ChatBubbleActionWrapperProps = {
  variant?: 'sent' | 'received';
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

const ChatBubbleActionWrapper = React.forwardRef<HTMLDivElement, ChatBubbleActionWrapperProps>(
    ({ variant, className, children, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                "bb:absolute bb:top-1/2 bb:-translate-y-1/2 bb:flex bb:opacity-0 bb:group-hover:opacity-100 bb:transition-opacity bb:duration-200",
                variant === "sent"
                    ? "bb:-left-1 bb:-translate-x-full bb:flex-row-reverse"
                    : "bb:-right-1 bb:translate-x-full",
                className,
            )}
            {...props}
        >
            {children}
        </div>
    ),
);
ChatBubbleActionWrapper.displayName = 'ChatBubbleActionWrapper';

export {
  ChatBubble,
  ChatBubbleAvatar,
  ChatBubbleMessage,
  ChatBubbleTimestamp,
  chatBubbleVariant,
  chatBubbleMessageVariants,
  ChatBubbleAction,
  ChatBubbleActionWrapper,
};
